import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import openaiService from '../services/openaiService';

const { FiUpload, FiDollarSign, FiTrendingUp, FiLock, FiExternalLink } = FiIcons;

function AITradeInEstimator() {
  const [vin, setVin] = useState('');
  const [vehicleDescription, setVehicleDescription] = useState('');
  const [estimateResult, setEstimateResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');

  const handleEstimate = async () => {
    if (!vin && !vehicleDescription) return;

    setIsCalculating(true);
    setEstimateResult(null);
    setAiAnalysis('');

    try {
      const vehicleInfo = {
        vin: vin || 'Not provided',
        description: vehicleDescription || 'Not provided',
        marketConditions: 'Current Halifax automotive market',
        location: 'Halifax, Nova Scotia'
      };

      // Get AI analysis of trade-in value
      const aiResponse = await openaiService.calculateTradeValue(vehicleInfo);
      setAiAnalysis(aiResponse);

      // Simulate calculation time
      setTimeout(() => {
        // Generate realistic estimate in CAD
        const baseValue = 35000;
        const marketAdjustment = 0.05; // 5% market boost
        const conditionFactor = 0.02; // 2% condition factor
        const lowValue = Math.round(baseValue * (1 - 0.1));
        const highValue = Math.round(baseValue * (1 + marketAdjustment + conditionFactor));

        setEstimateResult({
          lowValue,
          highValue,
          marketTrend: 'increasing',
          confidence: 94,
          factors: {
            marketDemand: 'High demand for this model in Halifax',
            condition: 'Based on description provided',
            seasonality: 'Favorable Maritime market timing',
            climate: 'Maritime climate wear considered'
          }
        });
        setIsCalculating(false);
      }, 3000);
    } catch (error) {
      console.error('Trade-in estimation error:', error);
      setIsCalculating(false);
      setAiAnalysis("I'm having trouble analyzing your vehicle right now. Please visit shawnryder.com for more information or call our Halifax location directly!");
    }
  };

  return (
    <section id="trade-in" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-white mb-4">
            Halifax AI Trade-In Estimator
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Get an instant, accurate trade-in estimate in CAD powered by Halifax market data and advanced AI analysis for Maritime conditions.
          </p>
          <div className="mt-4">
            <a 
              href="https://shawnryder.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 text-blue-300 hover:text-blue-200 transition-colors"
            >
              <span className="text-sm">Advanced Halifax AI by Shawn Ryder Digital</span>
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </a>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={FiUpload} className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">Halifax Vehicle Information</h3>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  VIN Number (Recommended for Halifax accuracy)
                </label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  placeholder="Enter your VIN for instant lookup"
                  className="w-full p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="text-center text-gray-400">
                <span>OR</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Describe Your Vehicle
                </label>
                <textarea
                  value={vehicleDescription}
                  onChange={(e) => setVehicleDescription(e.target.value)}
                  placeholder="e.g., '2019 Honda Civic LX, 75,000 kilometres, excellent condition, no accidents, always garaged in Halifax'"
                  className="w-full p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none resize-none h-32"
                />
              </div>
              <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
                <h4 className="text-blue-300 font-medium mb-2">Halifax AI Analysis Includes:</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Real-time Maritime market conditions</li>
                  <li>• Halifax comparable vehicle pricing</li>
                  <li>• Seasonal Nova Scotia demand factors</li>
                  <li>• Maritime climate wear assessment</li>
                </ul>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleEstimate}
                disabled={(!vin && !vehicleDescription) || isCalculating}
                className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiDollarSign} className="w-5 h-5" />
                <span>{isCalculating ? 'Halifax AI Analyzing...' : 'Get Halifax AI Estimate'}</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            {isCalculating ? (
              <div className="flex flex-col items-center justify-center h-full space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                <p className="text-gray-300">Halifax AI analyzing Maritime market conditions...</p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                </div>
                <p className="text-xs text-gray-400">Processing Halifax comparable sales data...</p>
              </div>
            ) : estimateResult ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Halifax AI Market Estimate</h3>
                  <div className="text-4xl font-bold text-blue-400">
                    ${estimateResult.lowValue.toLocaleString()} - ${estimateResult.highValue.toLocaleString()} CAD
                  </div>
                  <p className="text-gray-300 mt-2">{estimateResult.confidence}% Halifax confidence</p>
                </div>

                {aiAnalysis && (
                  <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                    <h4 className="text-blue-300 font-medium mb-2">Halifax AI Market Analysis:</h4>
                    <p className="text-gray-200 text-sm whitespace-pre-wrap">{aiAnalysis}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-700 rounded-lg p-4 text-center">
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-300">Halifax Market Trend</p>
                    <p className="text-green-400 font-semibold capitalize">{estimateResult.marketTrend}</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4 text-center">
                    <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-300">Best Halifax Offer</p>
                    <p className="text-blue-400 font-semibold">${estimateResult.highValue.toLocaleString()} CAD</p>
                  </div>
                </div>

                {estimateResult.factors && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-3">Halifax Value Factors:</h4>
                    <div className="space-y-2 text-sm">
                      {Object.entries(estimateResult.factors).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-300 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                          <span className="text-gray-200">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-6 py-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiLock} className="w-5 h-5" />
                  <span>Lock Your Halifax Offer</span>
                </motion.button>
                <p className="text-xs text-gray-400 text-center">
                  * Halifax AI estimate based on current Maritime market analysis. Final offer subject to vehicle inspection. Powered by{' '}
                  <a 
                    href="https://shawnryder.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    shawnryder.com
                  </a>
                </p>
              </motion.div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <SafeIcon icon={FiDollarSign} className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">Enter your Halifax vehicle information to get started</p>
                  <div className="text-sm text-gray-500">
                    <p className="mb-2">🤖 Our Halifax AI considers:</p>
                    <p>• Current Maritime market demand</p>
                    <p>• Halifax comparable vehicle sales</p>
                    <p>• Nova Scotia seasonal pricing trends</p>
                    <p>• Maritime climate condition factors</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AITradeInEstimator;